import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faArrowDown,
  faArrowUp,
  faInfoCircle,
  faMinus,
} from "@fortawesome/sharp-regular-svg-icons"
import { Box, Stack, Typography } from "@mui/material"
import type React from "react"
import { LightTooltip } from "../LocationDetails/LocationRisk/Shared"
import { extraColors } from "../../utils/theme"
import {
  NWSS_EVENT_DETECTIONS_LABEL,
  NWSS_HISTORIC_DETECTIONS_LABEL,
  nwssScopedLabel,
  type NwssDrawerSampleScope,
} from "./nwssDisplayFormat"
import type { NwssSignalSamplesResult } from "./useNwssSignalSamples"

interface NwssStatBandProps {
  result: NwssSignalSamplesResult
  sampleScope: NwssDrawerSampleScope
}

const STAT_TOOLTIPS = {
  historicDetections:
    "All positive wastewater PCR results in the loaded sample history for this signal—not limited to the anomaly event window.",
  eventDetections:
    "Positive samples that define this signal—the detections tied to this anomaly event. Often fewer than historic positives because routine sampling continues outside the event.",
  positivity:
    "Share of samples that tested positive over the last 14 collection days (through the latest sample in this view). Trend compares that rate to the prior 14 days, aligned with the rolling detection chart.",
  sites:
    "Number of distinct monitoring sites with at least one sample in this view.",
  detectionDays:
    "Number of distinct collection dates on which at least one positive was recorded in this view.",
} as const

export const NwssStatBand: React.FC<NwssStatBandProps> = ({
  result,
  sampleScope,
}) => {
  const { stats, truncated } = result

  return (
    <Stack spacing={0.75}>
      <Stack
        direction="row"
        useFlexGap
        sx={{ flexWrap: "wrap", gap: 2, rowGap: 1.5 }}
      >
        {sampleScope === "historic" ? (
          <>
            <NwssStatItem
              label={NWSS_HISTORIC_DETECTIONS_LABEL}
              value={stats.detectionCount.toLocaleString()}
              tooltip={STAT_TOOLTIPS.historicDetections}
            />
            <NwssStatItem
              label={NWSS_EVENT_DETECTIONS_LABEL}
              value={stats.memberCount.toLocaleString()}
              tooltip={
                truncated
                  ? `${STAT_TOOLTIPS.eventDetections} Count may be incomplete when sampling history is very long.`
                  : STAT_TOOLTIPS.eventDetections
              }
            />
          </>
        ) : (
          <NwssStatItem
            label={NWSS_EVENT_DETECTIONS_LABEL}
            value={stats.memberCount.toLocaleString()}
            tooltip={
              truncated
                ? `${STAT_TOOLTIPS.eventDetections} Count may be incomplete when sampling history is very long.`
                : STAT_TOOLTIPS.eventDetections
            }
          />
        )}
        <NwssStatItem
          label={nwssScopedLabel(sampleScope, "sites")}
          value={String(stats.siteCount)}
          tooltip={STAT_TOOLTIPS.sites}
        />
        {sampleScope === "historic" && (
          <NwssPositivityStat
            positivityPct={stats.positivityPct}
            trendPct={stats.positivityTrendPct}
            label={nwssScopedLabel(sampleScope, "positivity (14d)")}
            tooltip={STAT_TOOLTIPS.positivity}
          />
        )}
        <NwssStatItem
          label={nwssScopedLabel(sampleScope, "detection days")}
          value={String(stats.detectionDays)}
          tooltip={STAT_TOOLTIPS.detectionDays}
        />
      </Stack>
    </Stack>
  )
}

interface NwssPositivityStatProps {
  positivityPct: number
  trendPct: number | null
  label: string
  tooltip: string
}

const NwssPositivityStat: React.FC<NwssPositivityStatProps> = ({
  positivityPct,
  trendPct,
  label,
  tooltip,
}) => (
  <Box sx={{ minWidth: 76 }}>
    <Stack direction="row" sx={{ alignItems: "center", gap: 0.5 }}>
      <Typography
        sx={{
          color: extraColors.white,
          fontWeight: 700,
          fontSize: "0.95rem",
          lineHeight: 1.2,
        }}
      >
        {`${Math.round(positivityPct)}%`}
      </Typography>
      {trendPct != null && <PositivityTrendBadge trendPct={trendPct} />}
    </Stack>
    <Stack direction="row" sx={{ alignItems: "center", gap: 0.25 }}>
      <LightTooltip arrow placement="top" title={tooltip}>
        <FontAwesomeIcon
          icon={faInfoCircle}
          style={{
            fontSize: 9,
            color: extraColors.status.navyLight,
            cursor: "help",
          }}
        />
      </LightTooltip>
      <Typography
        sx={{
          color: extraColors.translucent50,
          fontSize: "0.6rem",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        {label}
      </Typography>
    </Stack>
  </Box>
)

const PositivityTrendBadge: React.FC<{ trendPct: number }> = ({ trendPct }) => {
  const rounded = Math.round(trendPct)
  if (rounded === 0) {
    return (
      <FontAwesomeIcon
        icon={faMinus}
        style={{ fontSize: 10, color: extraColors.translucent50 }}
      />
    )
  }
  const up = rounded > 0
  return (
    <Typography
      component="span"
      sx={{
        fontSize: "0.6rem",
        fontWeight: 700,
        color: up ? extraColors.status.redLight : extraColors.status.greenLight,
        lineHeight: 1,
      }}
    >
      <FontAwesomeIcon
        icon={up ? faArrowUp : faArrowDown}
        style={{ fontSize: 8, marginRight: 2 }}
      />
      {up ? "+" : ""}
      {rounded}pp
    </Typography>
  )
}

interface NwssStatItemProps {
  label: string
  value: string
  tooltip: string
  valueColor?: string
}

const NwssStatItem: React.FC<NwssStatItemProps> = ({
  label,
  value,
  tooltip,
  valueColor = extraColors.white,
}) => (
  <Box sx={{ minWidth: 76 }}>
    <Typography
      sx={{
        color: valueColor,
        fontWeight: 700,
        fontSize: "0.95rem",
        lineHeight: 1.2,
      }}
    >
      {value}
    </Typography>
    <Stack direction="row" sx={{ alignItems: "center", gap: 0.25 }}>
      <LightTooltip arrow placement="top" title={tooltip}>
        <FontAwesomeIcon
          icon={faInfoCircle}
          style={{
            fontSize: 9,
            color: extraColors.status.navyLight,
            cursor: "help",
          }}
        />
      </LightTooltip>
      <Typography
        sx={{
          color: extraColors.translucent50,
          fontSize: "0.6rem",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        {label}
      </Typography>
    </Stack>
  </Box>
)
